import * as React from 'react';

import { Dispatch } from 'redux';
import { connect, useSelector } from 'react-redux';

import { CallHistoryMethodAction, push, goBack } from 'connected-react-router';

import styled from 'styled-components';

import { Release } from '@common/types/release/release';
import {
  termsSection,
  validateRelease,
} from '@webapp/utils/validators/release';

import { Translate } from 'react-localize-redux';

// Store
import AppState from '@webapp/store/state';
import { allValid } from '@webapp/store/assets/selectors';
import { trackList } from '@webapp/store/tracks/selectors';
import { ShowSnackbar } from '@webapp/store/ui/action-creators';
import { releaseCheckout } from '@webapp/store/release/selectors';
import { UpdateField } from '@webapp/store/release/action-creators';
import { PublishRelease } from '@webapp/store/release/action-creators';

// Components
import Flex from '@components/layout/flex';
import { getWidth } from '@components/styles';
import BackIcon from '@components/basics/icons/back';
import RightArrow from '@components/basics/icons/right-arrow';
import Typography from '@webapp/components/basics/typography';
import MotionOpacity from '@webapp/components/motion/motion-opacity';

import ButtonLoadingCtrl from '@webapp/controllers/button-loading';
import ReleasePreviewCtrl from '@webapp/controllers/release/release-preview';
import PreviewHint from '@webapp/controllers/release/release-preview/preview-hint';
import moment from 'moment';

interface ReleasePreviewPageProps {
  className?: string;
  tosAccepted: boolean;
  recordedAccepted: boolean;
  authorAccepted: boolean;
  stepValid: boolean;
  isLaptop: boolean;
  isMobile: boolean;
  artistId?: string;
  subscriptionPlan: string;
  release?: Partial<Release>;
  hasCheckout: boolean;
  updateTos: (value: boolean) => UpdateField<'tosAccepted'>;
  updateRecorded: (value: boolean) => UpdateField<'recordedAccepted'>;
  updateOwner: (value: boolean) => UpdateField<'authorAccepted'>;
  gotoCheckout: () => CallHistoryMethodAction;
  showValidationError: (url: string) => ShowSnackbar;
  showReleaseDateValidationError: (url: string) => ShowSnackbar;
  publish: (
    artistId: string,
    releaseId: string,
    release: Partial<Release>
  ) => PublishRelease;
  goTo: (url: string) => CallHistoryMethodAction;
  handleBack: () => CallHistoryMethodAction;

  publishEnabled: boolean;
}

const mapStateToProps = (state: AppState) => {
  const trackIds: string[] = trackList(
    state.tracks.tracks,
    state.release.release?.id
  )
    .map((t) => t.id)
    .filter((t) => !!t) as string[];

  const termsAccepted = state.release.release
    ? termsSection(state.release.release)
    : false;
  const tracksValid = trackList(
    state.tracks.tracks,
    state.release.release?.id
  ).every((t) => !t.status || t.status === 'valid');

  return {
    tosAccepted: !!state.release.release?.tosAccepted,
    recordedAccepted: !!state.release.release?.recordedAccepted,
    authorAccepted: !!state.release.release?.authorAccepted,
    stepValid: termsAccepted && tracksValid,
    isLaptop: state.ui.laptop,
    isMobile: state.ui.mobile,
    artistId: state.artist.active?.id,
    release: state.release.release,
    subscriptionPlan: state.subscriptionManager.summary?.artists
      .find((a) => a.id === state.artist.active?.id)
      ?.subscriptions.find((s) => ['distribution', 'trac-pro'].includes(s.type))
      ?.plan,
    hasCheckout: releaseCheckout(state).some(
      (item) => item.price.value > 0 && item.quantity > 0
    ),
    publishEnabled: allValid(state.assets, trackIds),
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  updateTos: (value: boolean) =>
    dispatch(new UpdateField<'tosAccepted'>('tosAccepted', value)),
  updateRecorded: (value: boolean) =>
    dispatch(new UpdateField<'recordedAccepted'>('recordedAccepted', value)),
  updateOwner: (value: boolean) =>
    dispatch(new UpdateField<'authorAccepted'>('authorAccepted', value)),
  gotoCheckout: () => dispatch(push('./upgrade')),
  publish: (artistId: string, releaseId: string, release: Partial<Release>) =>
    dispatch(new PublishRelease(artistId, releaseId, release)),
  goTo: (url: string) => dispatch(push(url)),
  handleBack: () => dispatch(goBack()),
  showValidationError: (url: string) =>
    dispatch(
      new ShowSnackbar('RELEASE.VALIDATION_ERROR.SNACKBAR', {
        title: <Translate id="RELEASE.VALIDATION_ERROR.SNACKBAR.TITLE" />,
        text: <Translate id="RELEASE.VALIDATION_ERROR.SNACKBAR.TEXT" />,
        action: 'Review',
        to: url,
        hideDismiss: true,
      })
    ),
  showReleaseDateValidationError: (url: string) =>
    dispatch(
      new ShowSnackbar('RELEASE.DATE_VALIDATION_ERROR.SNACKBAR', {
        title: <Translate id="RELEASE.VALIDATION_ERROR.SNACKBAR.TITLE" />,
        text: <Translate id="RELEASE.DATE_VALIDATION_ERROR.SNACKBAR.TITLE" />,
        action: 'Review',
        to: url,
        hideDismiss: true,
      })
    ),
});

export const ReleasePreviewPage = (props: ReleasePreviewPageProps) => {
  const [validating, setValidating] = React.useState(false);
  const { publishEnabled, updateTos, updateRecorded, updateOwner } = props;

  const isReleaseValid = useSelector((state: AppState) =>
    validateRelease(state)
  );

  React.useEffect(() => {
    if (publishEnabled) {
      updateTos(true);
      updateRecorded(true);
      updateOwner(true);
    }
  }, [publishEnabled, updateTos, updateRecorded, updateOwner]);

  const validationErrors = useSelector(
    (state: AppState) => state.release.validationErrors
  );

  const getBackUrl = () => {
    return `/release/${props.artistId}/${props.release?.id}/title`;
  };
  const getBackUrlReleaseDate = () => {
    return `/release/${props.artistId}/${props.release?.id}/dates`;
  };

  const handleNextStepClick = () => {
    setValidating(true);
    if (isReleaseValid) {
      if (props.artistId && props.release?.id) {
        const isSentBack =
          props.release.status === 'changes-needed' ||
          props.release.status === 'rejected';
        if (
          moment(props.release.releaseDate) < moment() &&
          !isSentBack &&
          !props.release.isPublished
        ) {
          props.showReleaseDateValidationError(getBackUrlReleaseDate());
          setValidating(false);
          return;
        }
        if (
          (props.subscriptionPlan?.toUpperCase().includes('PRO') &&
            props.release?.id) ||
          !props.hasCheckout
        ) {
          props.publish(props.artistId, props.release.id, props.release);
          setValidating(false);
          return;
        }
        return props.gotoCheckout();
      }
    } else {
      props.showValidationError(getBackUrl());
      setValidating(false);
    }
  };

  const goToPreviousPage = () => {
    props.handleBack();
  };

  const getButtonLabel = () => {
    if (validating) {
      return <Translate id="RELEASE.PREVIEW.BUTTON.VALIDATING" />;
    }

    if (
      props.hasCheckout &&
      !props.subscriptionPlan?.toUpperCase().includes('PRO')
    ) {
      return <Translate id="RELEASE.PREVIEW.BUTTON.CHECKOUT" />;
    }

    if (props.release?.status !== 'draft') {
      return <Translate id="RELEASE.PREVIEW.BUTTON.REPUBLISH" />;
    }

    return <Translate id="RELEASE.PREVIEW.BUTTON.PUBLISH" />;
  };

  const ButtonDisabled = !(
    props.stepValid &&
    props.artistId &&
    props.release?.id
  );

  const TermsWrapper = () => (
    <div className="terms-box">
      {validationErrors.length === 0 && (
        <div className="terms">
          <Typography className="terms-text" weight="700" type="small">
            <Translate id="RELEASE.PREVIEW.TERMS.TEXT.1" />
            <a
              href="/distribution-agreement"
              className="terms-link"
              target="_blank"
            >
              <Translate id="RELEASE.PREVIEW.TERMS.TEXT.2" />
            </a>
            <Translate id="RELEASE.PREVIEW.TERMS.TEXT.3" />
          </Typography>
        </div>
      )}
      {validationErrors.length > 0 && (
        <div className="validation">
          <Typography className="validation-text" weight="700" type="small">
            <Translate id="RELEASE.PREVIEW.VALIDATION_ERROR.TEXT" />
          </Typography>
          <ul>
            {validationErrors.map((i) => (
              <li>
                <Translate id={i.id} data={i.data} />
              </li>
            ))}
          </ul>
        </div>
      )}
      {props.publishEnabled ? (
        <StyledButtonLoadingCtrl
          disabled={ButtonDisabled}
          isLoading={validating}
          disableLoader={!validating}
          onClick={handleNextStepClick}
        >
          <Flex justifyContent="space-between" alignItems="center">
            <Typography
              className={`btn-text ${ButtonDisabled ? 'disabled' : ''}`}
            >
              {getButtonLabel()}
            </Typography>
            {!validating && (
              <RightArrow
                className={`btn-arrow ${ButtonDisabled ? 'disabled' : ''}`}
                isWhite
              />
            )}
          </Flex>
        </StyledButtonLoadingCtrl>
      ) : null}
    </div>
  );

  return (
    <MotionOpacity className={props.className}>
      <BackButtonWrapper isMobile={props.isMobile}>
        <BackIcon className="mb-40" onClick={goToPreviousPage} />
      </BackButtonWrapper>

      <ReleasePreviewCtrl />

      {!props.isLaptop ? (
        <FixedWrapper>
          <PreviewHint />
          <TermsWrapper />
        </FixedWrapper>
      ) : (
        <TermsWrapper />
      )}
    </MotionOpacity>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(ReleasePreviewPage)`
  padding-bottom: 100px;
  .terms-box {
    margin-top: 20px;

    .validation {
      padding: 20px;
      border-radius: 10px 10px 0 0;
      background-color: ${({ theme }) => theme.colors.pink};

      .validation-text {
        line-height: 18px !important;
      }

      ul {
        margin-top: 10px;
        padding-left: 15px;

        li {
          margin-bottom: 10px;
          color: ${({ theme }) => theme.colors.black};
          font-size: ${({ theme }) => theme.typography.span};

          &:last-child {
            margin-bottom: 0;
          }
        }
      }
    }
    .terms {
      padding: 20px;
      border-radius: 10px 10px 0 0;
      background-color: ${({ theme }) => theme.colors.box};

      .terms-text {
        line-height: 18px !important;
      }

      .terms-link {
        color: ${({ theme }) => theme.colors.error};
        opacity: 1;
        transition: opacity
          ${({ theme }) =>
            `${theme.transitions.duration} ${theme.transitions.type}`};

        &:hover {
          opacity: 0.5;
        }
      }
    }
  }
`);

const FixedWrapper = styled.div`
  overflow: scroll;
  position: fixed;
  top: 180px;
  right: 80px;
  width: ${getWidth(5)};
  height: calc(100% - 180px - 20px);
`;

const BackButtonWrapper = styled.div<{ isMobile: boolean }>`
  position: ${({ isMobile }) => (isMobile ? '' : 'fixed')};
  top: 180px;
  left: 80px;
`;

const StyledButtonLoadingCtrl = styled(ButtonLoadingCtrl)`
  border-radius: 0px;
  border-bottom-right-radius: 10px;
  border-bottom-left-radius: 10px;
  min-width: auto;
  width: 100%;
  height: 77px;
  max-height: none;
  margin-top: 3px;

  .btn-text {
    color: ${({ theme }) => theme.colors.white};
    &.disabled {
      color: ${({ theme }) => theme.colors.tertiary};
    }
  }

  .btn-arrow {
    &.disabled {
      path {
        fill: ${({ theme }) => theme.colors.tertiary};
      }
    }
  }
`;
